import {
  HTMLInputTypeAttribute,
  memo,
  useEffect,
  useRef,
  useState,
} from "react";

// import { Box, InputLabel, TextField } from "@mui/material";
// import styles from "./pin-input-component.module.scss";

/**
 * Props for Pin Input Component.
 * @interface
 * @name PinInputComponentProps
 * @kind interface
 * @exports
 */
export interface PinInputComponentProps {
  /**
   * Pin read from input.
   * @method
   * @name pin
   * @kind property
   * @memberof PinInputComponentProps
   */
  pin: Array<number | string>;
  /**
   * Callback fired when the pin changes.
   * @method
   * @name onPinChanged
   * @kind property
   * @memberof PinInputComponentProps
   * @param {number | string} pinEntry Pin.
   * @param {number} index Index of each pin character.
   * @returns {void}
   */
  onPinChanged: (pinEntry: number | string, index: number) => void;
  /**
   * Length of the pin.
   * @property
   * @name pinLength
   * @kind property
   * @memberof PinInputComponentProps
   * @type {number}
   */
  pinLength: number;
  /**
   * Label for the input.
   * @property
   * @name label
   * @kind property
   * @memberof PinInputComponentProps
   * @type {string}
   */
  label: string;
  /**
   * Standard HTML Attribute, for label classes.
   * @property
   * @name labelClassName
   * @kind property
   * @memberof PinInputComponentProps
   * @type {string}
   */
  labelClassName: string;
  /**
   * Description
   *
   * @property
   * @name inputClassName
   * @kind property
   * @memberof PinInputComponentProps
   * @type {string | undefined}
   */

  inputClassName?: string;
  /**
   * (optional)
   * Standard HTML Attribute, for container classes.
   * @property
   * @name pinContainerClass
   * @kind property
   * @memberof PinInputComponentProps
   * @type {string | undefined}
   */
  pinContainerClass?: string;
  /**
   * (optional)
   * Standard HTML Attribute, for validation message classes.
   * @property
   * @name validationClassName
   * @kind property
   * @memberof PinInputComponentProps
   * @type {string | undefined}
   */
  validationClassName?: string;
  /**
   * (optional)
   * If `true`, disables the input.
   * @property
   * @name isValidating
   * @kind property
   * @memberof PinInputComponentProps
   * @type {boolean | undefined}
   */
  isValidating?: boolean;

  readOnly?: boolean;
  /**
   * (optional)
   * If `true`, shows the validation message.
   * @default false
   * @property
   * @name hasError
   * @kind property
   * @memberof PinInputComponentProps
   * @type {boolean | undefined}
   */
  hasError?: boolean;
  /**
   * (optional)
   * Validation message.
   * @property
   * @name validationMessage
   * @kind property
   * @memberof PinInputComponentProps
   * @type {string | undefined}
   */
  validationMessage?: string;
  /**
   * (optional)
   * Standard HTML Attribute, for Input Type.
   * @property
   * @name type
   * @kind property
   * @memberof PinInputComponentProps
   * @type {HTMLInputTypeAttribute | undefined}
   */
  masked?: boolean;
  /**
   * Description
   *
   * @property
   * @name masked
   * @kind property
   * @memberof PinInputComponentProps
   * @type {boolean | undefined}
   */
  type?: HTMLInputTypeAttribute;
  /**
   * Id for the Pin Input Component.
   * @property
   * @name id
   * @kind property
   * @memberof PinInputComponentProps
   * @type {string}
   */
  id: string;
  /**
   * (optional)
   * If `true`, sets the focus to the first pin index.
   * @property
   * @name setFocus
   * @kind property
   * @memberof PinInputComponentProps
   * @type {boolean | undefined}
   */
  setFocus?: boolean;
  /**
   * Standard Focus Event Attribute.
   * @method
   * @name onBlur
   * @kind property
   * @memberof PinInputComponentProps
   * @returns {void}
   */
  onBlur: () => void;
}

const BACKSPACE_KEY = "Backspace";

/**
 * Memoized Pin Input Component.
 * @constant
 * @name PinInputComponent
 * @kind variable
 * @type {React.NamedExoticComponent<PinInputComponentProps>}
 */
const PinInputComponent: React.NamedExoticComponent<PinInputComponentProps> =
  memo(
    /**
     * Pin Input Component.
     * @function
     * @name PinInputComponent
     * @kind function
     * @memberof PinInputComponent
     * @param {PinInputComponentProps} props Props for Pin Input Component.
     * @returns {JSX.Element} Pin Input Component.
     */
    function PinInputComponent(props: PinInputComponentProps): JSX.Element {
      const inputsRef = useRef<HTMLInputElement[]>([]);
      const componentRef = useRef<HTMLDivElement | null>(null);
      const [clickedOutside, setClickedOutside] = useState(false);

      const changePinFocus = (pinIndex: number) => {
        const inputRef = inputsRef.current[pinIndex];

        if (inputRef) {
          inputRef.focus();
        }
      };

      const removeValuesFromArray = (valuesArray: string[], value: string) => {
        const valueIndex = valuesArray.findIndex((entry) => entry === value);

        if (valueIndex === -1) {
          return;
        }
        valuesArray.splice(valueIndex, 1);
      };

      const onChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        index: number
      ) => {
        const currentValue = event.target.value;
        if (isNaN(Number(currentValue)) || currentValue === " ") return;
        const previousValue = event.target.defaultValue;
        const valuesArray = currentValue.split("");
        removeValuesFromArray(valuesArray, previousValue);

        const value = valuesArray.pop();
        if (!value) {
          return;
        }

        const pinNumber = Number(value.trim());
        if (isNaN(pinNumber) || value.length === 0) {
          return;
        }

        if (!isNaN(pinNumber)) {
          props.onPinChanged(pinNumber, index);
          if (index < props.pinLength - 1) {
            changePinFocus(index + 1);
          } else {
            inputsRef.current[index]?.blur();
          }
        }
      };

      /**
       * @method onKeyDown
       * @param event
       * @param index
       * @description
       * If the BackSpace is pressed we change the focus to the previous pin text field.
       * @returns
       */
      const onKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        index: number
      ) => {
        if (event.key !== BACKSPACE_KEY) return;

        if (props.pin[index] === "") {
          changePinFocus(index - 1);
          props.onPinChanged("", index - 1);
        } else {
          props.onPinChanged("", index);
        }
      };

      /**
       * If clickOutside is set to true, it means that the user clicked
       * outside of the component, if so we call the onBlur method passed to the component
       */
      const handleBlur = () => {
        if (clickedOutside && props.onBlur) {
          props.onBlur();
        }
      };

      /**
       * Detects whenever we click outside of the component.
       * If we do we set the clickedOutside to true.
       */
      useEffect(() => {
        if (props.setFocus) changePinFocus(0);
      }, [props.isValidating, props.setFocus]);

      const handleClickOutside = (event: MouseEvent) => {
        if (
          componentRef.current &&
          !componentRef.current.contains(event.target as Node)
        ) {
          setClickedOutside(true);
        } else {
          setClickedOutside(false);
        }
      };

      useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }, []);

      const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

      return (
        <div className="w-full">
          {props.label && (
            <p
              className={`mb-1.5 text-xs font-medium text-neutral-600 ${
                props.labelClassName ?? ""
              }`}
            >
              {props.label}
            </p>
          )}

          <div
            className={`flex w-full ${props.pinContainerClass ?? ""}`}
            id={props.id}
            ref={componentRef}
            onBlur={handleBlur}
          >
            {Array.from({ length: props.pinLength }, (_, index) => {
              const currentVal = props.pin[index];
              const isFilled = currentVal !== undefined && currentVal !== "";
              const showBullet = props.masked && isFilled;

              const borderClass = (() => {
                if (props.isValidating) return "border-neutral-100";
                if (props.readOnly) return "border-neutral-100";
                if (props.hasError)
                  return "border-primary-300 hover:border-primary-300 focus:border-primary-300";
                return "border-neutral-300 hover:border-neutral-900 focus:border-neutral-900";
              })();

              const bgClass =
                props.isValidating || props.readOnly
                  ? "bg-neutral-25"
                  : "bg-white";

              const textClass =
                props.isValidating || props.readOnly
                  ? "text-neutral-500"
                  : "text-neutral-900";

              return (
                <div className="relative" key={index}>
                  <input
                    key={index}
                    ref={(el) => {
                      if (el) inputsRef.current[index] = el;
                    }}
                    id={`${props.id}--pin-input-${index}`}
                    name={`code-${index}`}
                    type="text"
                    autoComplete="off"
                    maxLength={1}
                    inputMode="numeric"
                    disabled={props.isValidating}
                    readOnly={props.readOnly}
                    value={showBullet ? "" : currentVal ?? ""}
                    onChange={(event) => onChange(event, index)}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
                      onKeyDown(event, index)
                    }
                    style={{ width: `100%` }}
                    className={[
                      "h-12 rounded-[10px] border text-center outline-none transition-colors duration-150",
                      showBullet
                        ? "text-[15px] leading-[48px] caret-transparent"
                        : "text-base",
                      bgClass,
                      textClass,
                      borderClass,
                      props.isValidating ? "cursor-not-allowed" : "",
                      props.inputClassName ?? "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  />

                  {showBullet && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="h-[10px] w-[10px] rounded-full bg-neutral-900" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-2 ml-2 min-h-[20px] w-full">
            {props.hasError && (
              <p
                className={`text-start text-xs text-[#DF320C] ${
                  props.validationClassName ?? ""
                }`}
              >
                {props.validationMessage}
              </p>
            )}
          </div>
        </div>
      );
    }
  );

export default PinInputComponent;
